import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import type { AISystemExecution, AISystemExecutionListItem } from '../../types';
import { aiSystemApi, executionApi } from '../../services/api';
import { ErrorAlert, LoadingSpinner, PageHeader } from '../common';
import ExecutionSummaryDialog from './ExecutionSummaryDialog';
import {
  formatMoney,
  formatRelativeTimestamp,
  formatRunDuration,
  formatTimestamp,
  getActionsSummary,
  getRunOutcomeSummary,
  getStatusLabel,
  getStatusTone,
} from './runsPresentation';

interface RunsWorkspaceProps {
  embedded?: boolean;
}

const RunsWorkspace: React.FC<RunsWorkspaceProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [aiSystem, setAiSystem] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [hasActions, setHasActions] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(!isMobile);
  const [selectedExecution, setSelectedExecution] = useState<AISystemExecution | null>(null);
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string>('');

  const { data: aiSystemsData } = useQuery({
    queryKey: ['ai-systems-list'],
    queryFn: () => aiSystemApi.list({ page_size: 1000 }),
  });

  const queryParams: Record<string, unknown> = {
    page: page + 1,
    page_size: pageSize,
  };

  if (aiSystem) {
    queryParams.ai_system = aiSystem;
  }
  if (status) {
    queryParams.status = status;
  }
  if (search.trim()) {
    queryParams.search = search.trim();
  }
  if (hasActions) {
    queryParams.has_actions = hasActions;
  }
  if (dateFrom) {
    queryParams.created_after = new Date(dateFrom).toISOString();
  }
  if (dateTo) {
    queryParams.created_before = new Date(dateTo).toISOString();
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['runs-workspace', queryParams],
    queryFn: () => executionApi.list(queryParams),
  });

  const { data: stats } = useQuery({
    queryKey: ['execution-stats'],
    queryFn: () => executionApi.stats(),
  });

  const currentPageRuns = data?.results ?? [];
  const currentPageSpend = useMemo(
    () => currentPageRuns.reduce((sum, execution) => sum + (execution.total_cost ?? 0), 0),
    [currentPageRuns],
  );
  const currentPageActions = useMemo(
    () => currentPageRuns.filter((execution) => (getActionsSummary(execution)?.total ?? 0) > 0).length,
    [currentPageRuns],
  );

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  const resetFilters = () => {
    setAiSystem('');
    setStatus('');
    setHasActions('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const handleRefresh = () => {
    setDetailError('');
    refetch();
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewExecution = async (execution: AISystemExecutionListItem) => {
    setDetailError('');
    setLoadingDetail(true);

    try {
      const fullExecution = await executionApi.get(execution.id);
      setSelectedExecution(fullExecution);
      setViewerOpen(true);
    } catch (detailLoadError) {
      console.error('Failed to fetch run details:', detailLoadError);
      setDetailError('Unable to load that run right now. Refresh and try again.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedExecution(null);
  };

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {isMobile && (
        <Tooltip title={filtersExpanded ? 'Hide Filters' : 'Show Filters'}>
          <IconButton onClick={() => setFiltersExpanded(!filtersExpanded)} color="primary" size="small">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Refresh">
        <IconButton onClick={handleRefresh} color="primary" size="small">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderOutcomeText = (execution: AISystemExecutionListItem) => {
    const outcome = getRunOutcomeSummary(execution);

    return (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {outcome.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {outcome.detail}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {embedded ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {headerActions}
        </Box>
      ) : (
        <PageHeader title="Runs" actions={headerActions} />
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Outcome-first runs view
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Scan what ran, what it changed, and what it cost. Low-level diagnostics stay tucked into each run instead of filling the main table.
        </Typography>
      </Paper>

      {detailError && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'error.light' }}>
          <Typography variant="body2" color="error.main">
            {detailError}
          </Typography>
        </Paper>
      )}

      {stats && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption">
                  Total runs
                </Typography>
                <Typography variant="h5">{stats.total_executions}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption">
                  Success rate
                </Typography>
                <Typography variant="h5" color="success.main">
                  {stats.success_rate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption">
                  Runs with actions
                </Typography>
                <Typography variant="h5">{currentPageActions}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Current page
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="caption">
                  Current page spend
                </Typography>
                <Typography variant="h5">{formatMoney(currentPageSpend, { compact: true })}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Visible results only
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: filtersExpanded ? 2 : 0,
              cursor: 'pointer',
            }}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FilterListIcon fontSize="small" /> Filters
            </Typography>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}

        <Collapse in={!isMobile || filtersExpanded}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="System or run"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={aiSystemsData?.results || []}
                getOptionLabel={(option: any) => option.name || ''}
                value={aiSystemsData?.results?.find((system: any) => String(system.id) === aiSystem) || null}
                onChange={(_event, newValue) => {
                  setAiSystem(newValue ? String(newValue.id) : '');
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="AI system" placeholder="All" size="small" />}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="start">Start</MenuItem>
                <MenuItem value="running">Running</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Outcome"
                value={hasActions}
                onChange={(event) => {
                  setHasActions(event.target.value);
                  setPage(0);
                }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">With actions</MenuItem>
                <MenuItem value="no">No actions</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="From"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="To"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={resetFilters}
                size="small"
                sx={{ height: '40px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {isLoading ? (
        <LoadingSpinner message="Loading runs..." />
      ) : isMobile ? (
        <Box>
          <Grid container spacing={1.5}>
            {currentPageRuns.map((execution) => {
              const outcome = getRunOutcomeSummary(execution);

              return (
                <Grid size={{ xs: 12 }} key={execution.id}>
                  <Card>
                    <CardActionArea onClick={() => handleViewExecution(execution)}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {execution.system_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {execution.execution_type_display}
                            </Typography>
                          </Box>
                          <Chip
                            label={getStatusLabel(execution.status, execution.status_display)}
                            size="small"
                            color={getStatusTone(execution.status) as 'default' | 'success' | 'error' | 'warning' | 'info'}
                          />
                        </Box>

                        <Typography variant="body2" fontWeight="medium">
                          {outcome.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {outcome.detail}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Started
                            </Typography>
                            <Typography variant="body2">{formatRelativeTimestamp(execution.created_at)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Duration
                            </Typography>
                            <Typography variant="body2">{formatRunDuration(execution)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total cost
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                              {formatMoney(execution.total_cost, { compact: true })}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {currentPageRuns.length === 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No runs match these filters.
                </Typography>
              </CardContent>
            </Card>
          )}

          <TablePagination
            component="div"
            count={data?.count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Box>
      ) : (
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>System</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Total cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageRuns.map((execution) => (
                  <TableRow
                    key={execution.id}
                    hover
                    onClick={() => handleViewExecution(execution)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {execution.system_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {execution.execution_type_display}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(execution.status, execution.status_display)}
                        size="small"
                        color={getStatusTone(execution.status) as 'default' | 'success' | 'error' | 'warning' | 'info'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(execution.created_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTimestamp(execution.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatRunDuration(execution)}</Typography>
                    </TableCell>
                    <TableCell>{renderOutcomeText(execution)}</TableCell>
                    <TableCell>
                      <Tooltip title={execution.total_cost != null ? formatMoney(execution.total_cost) : 'No cost recorded'}>
                        <Typography variant="body2" fontWeight="medium" color="primary.main">
                          {formatMoney(execution.total_cost, { compact: true })}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {currentPageRuns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No runs match these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      )}

      <Backdrop sx={{ color: '#fff', zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }} open={loadingDetail}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {selectedExecution && (
        <ExecutionSummaryDialog open={viewerOpen} onClose={handleCloseViewer} execution={selectedExecution} />
      )}
    </Box>
  );
};

export default RunsWorkspace;